// CANONICAL COPY (GH #7115). src/utils/evidenceRecurrence.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/utils/evidenceRecurrence.js
 *
 * GH #6661 — one question, asked in four places: does this evidence
 * requirement RECUR, and if so, how often?
 *
 * It matters because `validUntil` being absent means two opposite things
 * depending on the answer:
 *
 *   a one-time requirement (on-hire NDA, ID check, background check)
 *     — no expiry is HONEST. The record is current, forever, correctly.
 *
 *   a recurring requirement (annual security awareness, quarterly access
 *   review) — no expiry is a MISSING FIELD. Reading it as "never expires"
 *     turns a recurring governance activity into a one-off box tick, which
 *     is TRYGGR_INTENT.md §12's named failure: monitoring is not an event.
 *
 * Three compliance-engine checkers read `!r.validUntil` as current for both
 * cases. This module is the test that tells them apart.
 *
 * DUPLICATED, deliberately, in `azure-functions/shared/evidence/scheduler.js`
 * — nothing under `azure-functions/` may import from `src/`, the same
 * constraint that produced `trainingDerivations.js`'s twin. Pinned by
 * `azure-functions/__tests__/training-validity-parity.test.js`.
 */

// The intervals `EvidenceRequirementsSettings.jsx`'s FREQUENCY_OPTIONS offers,
// minus the two that carry no fixed number: `one-time` (does not recur) and
// `custom` (carries its own `frequencyDays`).
const FREQUENCY_DAYS = { annual: 365, biennial: 730, quarterly: 90 };

/**
 * Does this requirement recur at all?
 *
 * THE SAME TEST the sweeper's RENEW step already applies
 * (`if (!req.frequency || req.frequency === 'one-time') continue`), so the
 * engine and the renewal machinery cannot disagree about which requirements
 * are recurring. `frequency` is only written when `trigger === 'recurring'`,
 * so its presence is the recurrence flag.
 *
 * Deliberately NOT `recurrenceDays(req) !== null`: a `custom` requirement with
 * a missing or garbage `frequencyDays` still recurs — we just cannot say how
 * often. Collapsing the two would make a malformed recurring requirement read
 * as one-time, which is the fail-OPEN direction.
 */
function requirementRecurs(req) {
  return Boolean(req && req.frequency && req.frequency !== 'one-time');
}

/**
 * How many days one cycle of this requirement lasts, or null when that cannot
 * be derived — because it does not recur, or because a `custom` interval is
 * missing or unusable.
 *
 * A null from a requirement that DOES recur is not a licence to treat the
 * record as current: `requirementRecurs` still says yes, the checker still
 * refuses to count an unstamped record, and the backfill leaves the record
 * alone rather than guessing. It surfaces as a gap, which is honest.
 */
function recurrenceDays(req) {
  if (!requirementRecurs(req)) return null;
  if (req.frequency === 'custom') {
    const days = Number(req.frequencyDays);
    return Number.isFinite(days) && days > 0 ? days : null;
  }
  return FREQUENCY_DAYS[req.frequency] ?? null;
}

module.exports = {
  FREQUENCY_DAYS,
  requirementRecurs,
  recurrenceDays,
};
